import * as React from 'react';

import classNames from 'classnames';

import type { CmlContent } from 'bundles/cml';
import { questions } from 'bundles/discussions/constants';

import _t from 'i18n!nls/discussions';

import 'css!./__styles__/NewThreadForm';

type ForumObject = {
  id: string;
  title: string;
  parentForumId?: string;
};

type ChangeEvent = {
  value: string | CmlContent | ForumObject;
  isValid: boolean;
};

type TitleInputProps = {
  id?: string;
  handleInputChange: (type: string, event: ChangeEvent) => void;
  value: string;
  hasError: boolean;
  elemRefHook?: (elem: HTMLElement | null) => void;
};

const MIN_TITLE_LEN = questions.thought.minChars;
const MAX_TITLE_LEN = questions.thought.maxChars;

class TitleInput extends React.Component<TitleInputProps> {
  declare elemRef: HTMLElement | null;

  componentDidMount() {
    // This is a hack because the CML editor partially steals focus
    setTimeout(() => this.elemRef && this.elemRef.focus(), 0);
  }

  handleInputChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { handleInputChange } = this.props;
    if (e.currentTarget.value.length <= MAX_TITLE_LEN) {
      const event = {
        value: e.currentTarget.value,
        isValid: this.validate(e.currentTarget.value),
      };
      handleInputChange('title', event);
    }
  };

  getErrorMessage = (key: string | null) => {
    const translatedStrings: Record<string, string> = {
      TOO_SHORT: _t('Error: Please enter a longer title'),
    };
    return key ? translatedStrings[key] : '';
  };

  checkForErrors = (newValue: string) => {
    if (newValue.length <= MIN_TITLE_LEN) {
      return 'TOO_SHORT';
    }
    return null;
  };

  validate(newValue: string) {
    return this.checkForErrors(newValue) === null;
  }

  setElemRef = (elem: HTMLElement | null) => {
    const { elemRefHook } = this.props;
    this.elemRef = elem;
    if (typeof elemRefHook === 'function') {
      elemRefHook(elem);
    }
  };

  renderError = (errorMessage?: string) => {
    const { value, id } = this.props;
    return (
      <div className="c-form-error-message color-danger" id={`${id}-error-message`}>
        {errorMessage || this.getErrorMessage(this.checkForErrors(value))}
      </div>
    );
  };

  render() {
    const { id, value, hasError } = this.props;
    const classes = classNames({
      'c-form-input': true,
      'c-has-error': hasError,
    });

    return (
      <div className="c-form">
        <input
          aria-describedby={`${id}-error-message`}
          aria-invalid={hasError}
          aria-label={_t('Title')}
          aria-required={true}
          className={classes}
          id={id}
          onChange={this.handleInputChange}
          placeholder={_t('Write a descriptive title')}
          ref={this.setElemRef}
          required={true}
          title={_t('Title')}
          value={value}
        />
        {hasError && this.renderError()}
      </div>
    );
  }
}

export default {
  TitleInput,
};

export { TitleInput };
